import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./store";
import { useTheme, useThemeInit } from "./theme";
import {
  LayoutDashboard,
  Activity,
  Wrench,
  ListVideo,
  Users,
  BarChart3,
  Settings as Cog,
  ScrollText,
  HardDrive,
  Instagram,
  Handshake,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
} from "lucide-react";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/admin/health", label: "Health", icon: Activity, testid: "nav-health" },
  { to: "/admin/operations", label: "Operations", icon: Wrench, testid: "nav-operations" },
  { to: "/admin/jobs", label: "Jobs", icon: ListVideo, testid: "nav-jobs" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-users" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/admin/settings", label: "Settings", icon: Cog, testid: "nav-settings" },
  { to: "/admin/ig-validation", label: "IG Validation", icon: Instagram, testid: "nav-ig-validation" },
  { to: "/admin/affiliates", label: "Affiliates", icon: Handshake, testid: "nav-affiliates" },
  { to: "/admin/audit", label: "Audit log", icon: ScrollText, testid: "nav-audit" },
  { to: "/admin/storage", label: "Storage", icon: HardDrive, testid: "nav-storage" },
];

export default function Layout() {
  useThemeInit();
  useAdminPwaInstall();
  const { admin, env, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Mobile sidebar drawer state. Closed by default. Auto-closes on route
  // change so tapping a nav link dismisses the overlay.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open so background doesn't move
  // when the user is tapping nav items on iOS.
  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [mobileNavOpen]);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* ──────── DESKTOP SIDEBAR (≥lg) ──────── */}
      <aside
        className="hidden lg:flex w-56 border-r border-border flex-col shrink-0"
        data-testid="admin-sidebar"
      >
        <Brand />
        <NavList />
        <SignOut onLogout={onLogout} />
      </aside>

      {/* ──────── MOBILE DRAWER (<lg) ──────── */}
      {mobileNavOpen && (
        <>
          <button
            aria-label="Close menu"
            onClick={() => setMobileNavOpen(false)}
            className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            data-testid="admin-mobile-backdrop"
          />
          <aside
            className="lg:hidden fixed top-0 left-0 z-50 w-64 max-w-[85vw] h-full bg-background border-r border-border flex flex-col shadow-2xl"
            data-testid="admin-mobile-sidebar"
          >
            <Brand>
              <button
                onClick={() => setMobileNavOpen(false)}
                className="p-1.5 text-muted-foreground hover:text-foreground"
                aria-label="Close menu"
                data-testid="admin-mobile-close"
              >
                <X className="w-5 h-5" />
              </button>
            </Brand>
            <NavList />
            <SignOut onLogout={onLogout} />
          </aside>
        </>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="h-14 border-b border-border flex items-center justify-between gap-3 px-4 sm:px-6 sticky top-0 bg-background/80 backdrop-blur-md z-30"
          data-testid="admin-topnav"
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden p-1.5 -ml-1.5 text-muted-foreground hover:text-foreground"
              aria-label="Open menu"
              data-testid="admin-mobile-open"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 sm:gap-3 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground min-w-0">
              <EnvBadge env={env} />
              <span className="hidden md:inline">·</span>
              <span className="hidden md:inline truncate">{new Date().toUTCString().slice(5, 22)} UTC</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 text-sm shrink-0">
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
              title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
              className="inline-flex items-center justify-center w-7 h-7 border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
            >
              {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
            {admin?.role === "owner" && (
              <span
                data-testid="role-badge"
                className="hidden sm:inline-flex items-center px-2 py-0.5 border border-emerald-500/40 text-emerald-400 text-[10px] font-mono uppercase tracking-[0.2em]"
                title="Owner role — full infrastructure controls"
              >
                owner
              </span>
            )}
            <span className="hidden md:inline text-muted-foreground font-mono text-xs truncate max-w-[200px]">
              {admin?.email}
            </span>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ── Sidebar sub-components — shared between desktop and mobile drawer ──

function Brand({ children }) {
  return (
    <div className="px-5 py-5 border-b border-border flex items-center justify-between">
      <div>
        <div className="font-display text-xl font-medium text-foreground tracking-tight">
          Clipra
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mt-0.5">
          // admin
        </div>
      </div>
      {children}
    </div>
  );
}

function NavList() {
  return (
    <nav className="flex-1 py-3 overflow-y-auto">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={item.testid}
          className={({ isActive }) =>
            `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 ${
              isActive
                ? "border-foreground text-foreground bg-secondary/40"
                : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/20"
            }`
          }
        >
          <item.icon className="w-4 h-4" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SignOut({ onLogout }) {
  return (
    <div className="p-3 border-t border-border">
      <button
        onClick={onLogout}
        className="w-full flex items-center gap-3 px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        data-testid="admin-logout-btn"
      >
        <LogOut className="w-4 h-4" />
        <span>Sign out</span>
      </button>
    </div>
  );
}

function EnvBadge({ env }) {
  const isProd = env === "PROD";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 border text-[10px] font-mono tracking-[0.18em] ${
        isProd
          ? "border-red-500/50 text-red-400"
          : "border-amber-500/40 text-amber-400"
      }`}
      data-testid="env-badge"
    >
      <span
        className={`live-dot inline-block w-1.5 h-1.5 rounded-full ${
          isProd ? "bg-red-500" : "bg-amber-400"
        }`}
      />
      {env}
    </span>
  );
}

// PWA wiring — admin-only. Injects a <link rel="manifest"> when the
// admin layout mounts (so the landing page isn't affected by it) and
// registers a passthrough service worker for iOS "Add to Home Screen"
// installability. Removes the manifest link on unmount so SPA-routed
// navigation away from /admin doesn't keep advertising the admin PWA.
function useAdminPwaInstall() {
  useEffect(() => {
    let link = document.querySelector('link[rel="manifest"][data-admin="1"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      link.href = "/admin-manifest.webmanifest";
      link.dataset.admin = "1";
      document.head.appendChild(link);
    }
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/admin-sw.js", { scope: "/admin" })
        .catch(() => { /* installability is best-effort */ });
    }
    return () => {
      if (link && link.parentNode) link.parentNode.removeChild(link);
    };
  }, []);
}

